import React, { useState } from 'react'

import { css } from 'design'
import { Button, buttonStyles } from 'primitives/button'
import { cn } from 'util/cn'

// eslint-disable-next-line
// @ts-ignore
// import ErrorImage from '../../../assets/images/error.svg'
import { isLiveChatApp } from '../../reduxes/slices/live_chat_app/selectors'
import { useSelectorDelegateStore } from '../../reduxes/stores/root_store/hooks'

export type ErrorDetailProps = {
  error: Error
}

export const ErrorDetail = ({ error }: ErrorDetailProps) => {
  const [showStackTrace, setShowStackTrace] = useState<boolean>(!!process.env.VITEST_WORKER_ID)
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  const reload = () => {
    if (liveChatApp) {
      window.parent.location.reload()
    } else {
      window.location.reload()
    }
  }

  return (
    <section className={cn('error-boundary', errorBoundaryStyles())}>
      <h2 className={cn('header', headerStyles())}>ERROR</h2>
      <aside className={cn('error-banner', errorBannerStyles())}>
        {/*eslint-disable-next-line*/}
        {/*@ts-ignore*/}
        {/*{!window.vitest && <ErrorImage/>}*/}
      </aside>
      <h3 className={cn('sub-header', subHeaderStyles())}>
        Hmmm... something&apos;s not right.&nbsp;
        <br />
        Let&apos;s pretend this didn&apos;t happen.
      </h3>
      <Button variant="primary" shape="pill" title="Reload" onClick={reload}>
        Reload
      </Button>

      <section className={cn('addendum-message', addendumMessageStyles())}>
        <span>Feeling techy?</span>
        <button
          className={cn('btn-link', linkButtonStyles())}
          onClick={() => setShowStackTrace((s) => !s)}
        >
          {`${showStackTrace ? 'Hide' : 'Show'} crash report`}
        </button>
      </section>
      {showStackTrace && (
        <div className={stackTraceStyles()}>
          <section className={stackTraceInnerStyles()}>
            <p>Here’s what we know…</p>
            <p>
              <strong>Error:</strong> {error.toString()}
            </p>
            <p>
              <strong>Stacktrace:</strong> {error.stack}
            </p>

            <a
              href={'mailto:' + (window as any).supportEmail}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                buttonStyles({ variant: 'primary', shape: 'pill' }),
                contactSupportButtonStyles(),
              )}
            >
              Want to contact support?
            </a>
          </section>
        </div>
      )}
    </section>
  )
}

const errorBoundaryStyles = css({
  color: '$grey950',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  padding: '$5 0 0',

  boxSizing: 'border-box',

  overflowY: 'auto',

  '*': {
    boxSizing: 'border-box',
  },

  '> * + *': {
    marginTop: '$5',
  },
})

const headerStyles = css({
  fontSize: '$9',
  fontWeight: 500,
  textAlign: 'center',
  color: '$grey500',
  margin: 0,

  '@sm': {
    fontSize: '$11',
  },
})

const errorBannerStyles = css({
  '> svg': {
    width: '100%',
    height: 'auto',
  },
})

const subHeaderStyles = css({
  fontSize: '$5',
  fontWeight: 'bold',
  textAlign: 'center',
  color: '$grey700',
  marginBottom: 0,

  '@sm': {
    fontSize: '$7',
  },
})

const linkButtonStyles = css({
  background: 'none',
  border: 'none',
  padding: 0,
  color: '$accent500',
  textDecoration: 'underline',
})

const addendumMessageStyles = css({
  textAlign: 'center',

  '> * + *': {
    marginLeft: '$3',
  },
})

const stackTraceStyles = css({
  display: 'flex',
  flex: 1,
  width: '100%',
  minHeight: 200,
  backgroundColor: '$grey50',
  padding: '$4 $4 0',
  fontSize: '$1',
  overflow: 'auto',
  boxSizing: 'border-box',
})

const stackTraceInnerStyles = css({
  width: '100%',

  '> p': {
    marginBottom: 0,
  },

  '> * + *': {
    marginTop: '$3',
  },
})

const contactSupportButtonStyles = css({
  width: 'auto',
  textDecoration: 'none',
})
